import { useEffect, useLayoutEffect, useState } from "react";
import type { CSSProperties, RefObject } from "react";
import { createPortal } from "react-dom";
import { Package } from "lucide-react";
import { api } from "../../api/client";
import { formatMoney } from "../../lib/money";
import type { CatalogHit } from "../../api/types";

// 明細列「摘要」欄的就地建議。品項庫只是加速輸入,不構成約束:
// 帶入之後每一格仍可自由改寫(業主的項目五花八門,不一定在規範品項內)。

/** 打幾個字才送出搜尋。與 ⌘K 全域搜尋一致,避免一個字就打爆後端。 */
export const SUGGEST_MIN_LENGTH = 2;
/** 送出前的等待時間,邊打邊搜但不是每個鍵都送。 */
export const SUGGEST_DEBOUNCE_MS = 300;
/** 下拉最多幾筆,超過就請使用者再打精確一點。 */
const SUGGEST_LIMIT = 8;

/**
 * 依關鍵字向品項庫要建議。字數不足時直接清空,不打後端。
 * 後端萬一回了非陣列(例如錯誤頁),一律當成沒有建議,不讓畫面炸掉。
 */
export function useCatalogSuggest(query: string): CatalogHit[] {
  const [hits, setHits] = useState<CatalogHit[]>([]);
  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed.length < SUGGEST_MIN_LENGTH) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get<CatalogHit[]>("/catalog/search", { q: trimmed, limit: SUGGEST_LIMIT })
        .then((result) => {
          if (!cancelled) setHits(Array.isArray(result) ? result : []);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        });
    }, SUGGEST_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [trimmed]);

  return hits;
}

/** 下拉的最大高度(對應 max-h-56 的 14rem),翻上翻下都以它估算。 */
const MAX_HEIGHT = 224;
/** 下拉的最小寬度,免得摘要欄很窄時建議擠成一條。 */
const MIN_WIDTH = 288;

export interface InlineSuggestProps {
  /** 下拉清單的 id,供輸入框的 aria-controls 指過來。 */
  id: string;
  hits: CatalogHit[];
  activeIndex: number;
  onPick: (hit: CatalogHit) => void;
  onHover: (index: number) => void;
  /** 對齊的輸入框。清單以它的位置定位。 */
  anchorRef: RefObject<HTMLElement | null>;
}

/**
 * 依輸入框的位置算出下拉的固定座標。
 *
 * **一定要 portal 到 body + position: fixed。** 明細表的外層是
 * `overflow-x-auto`,而 CSS 規範說:overflow-x 不是 visible 時,overflow-y 的
 * visible 會被計算成 auto —— 絕對定位的下拉會被那個容器裁掉,章節只有一兩列時
 * (新增章節後的常態)幾乎看不到任何建議,只能靠盲按 ↑↓ Enter。
 */
function useAnchorPosition(anchorRef: RefObject<HTMLElement | null>, open: boolean): CSSProperties | null {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") {
      setStyle(null);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor || typeof anchor.getBoundingClientRect !== "function") return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, MIN_WIDTH), Math.max(window.innerWidth - 16, MIN_WIDTH));
      const spaceBelow = window.innerHeight - rect.bottom;
      // 下方塞不下就翻到輸入框上面,不要讓清單掉出視窗外
      const flipUp = spaceBelow < MAX_HEIGHT && rect.top > spaceBelow;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setStyle({
        position: "fixed",
        left,
        width,
        maxHeight: MAX_HEIGHT,
        ...(flipUp ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) } : { top: rect.bottom + 4 }),
      });
    };
    update();
    // 捲動時要跟著走。用捕獲階段才收得到表格容器自己的捲動。
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorRef, open]);

  return style;
}

/** 建議項目的 DOM id,輸入框用 aria-activedescendant 指向它。 */
export function suggestOptionId(listId: string, variantId: number): string {
  return `${listId}-${variantId}`;
}

export function InlineSuggest({ id, hits, activeIndex, onPick, onHover, anchorRef }: InlineSuggestProps) {
  const style = useAnchorPosition(anchorRef, hits.length > 0);

  if (hits.length === 0 || style === null || typeof document === "undefined") return null;

  const list = (
    <ul
      id={id}
      role="listbox"
      aria-label="品項庫建議"
      style={style}
      className={
        "z-50 m-0 list-none overflow-y-auto rounded-[var(--radius)] border " +
        "border-[var(--border-strong)] bg-[var(--surface)] p-1 shadow-card"
      }
    >
      {hits.map((hit, index) => {
        const active = index === activeIndex;
        return (
          <li key={hit.variant_id} role="presentation" className="m-0">
            <div
              id={suggestOptionId(id, hit.variant_id)}
              role="option"
              aria-selected={active}
              // 用 mousedown 才不會先讓輸入框失焦而收起清單。
              onMouseDown={(event) => {
                event.preventDefault();
                onPick(hit);
              }}
              onMouseEnter={() => onHover(index)}
              className={
                "flex cursor-pointer items-center gap-2 rounded-[var(--radius)] px-2 py-1.5 text-sm " +
                (active ? "bg-[var(--accent-soft)] text-[var(--accent-hover)]" : "text-[var(--text)]")
              }
            >
              <Package aria-hidden="true" size={14} className="shrink-0 text-[var(--text-3)]" />
              <span className="min-w-0 flex-1 break-words">
                <span className="block">{hit.item_name}</span>
                {hit.spec ? <span className="block text-xs text-[var(--text-3)]">{hit.spec}</span> : null}
              </span>
              <span className="tabular shrink-0 text-xs text-[var(--text-2)]">{formatMoney(hit.price)}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );

  return createPortal(list, document.body);
}
